/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module Api
 *
 * @author Anoop SS
 */
"use server";

import SaleorClient from "./SaleorClient";
import {
  AddToWishlistDocument,
  AddToWishlistMutation,
  CreateWishlistDocument,
  CreateWishlistMutation,
  CustomerSubscriptionActivateDocument,
  CustomerSubscriptionActivateMutation,
  MergeWishlistDocument,
  MergeWishlistMutation,
  RemoveFromWishlistDocument,
  RemoveFromWishlistMutation,
  WishlistDetailsDocument,
  WishlistDetailsQuery,
  WishlistItemInput,
  WishlistProductsDocument,
} from "../generated/graphql";
import {
  WishlistCreateResponse,
  WishlistResponse,
  wishlistItemDeleteResponse,
  wishlistLinesAddResponse,
} from "../../../common/lib/types";
import { revalidatePath } from "next/cache";
import { getWishlistIdByLocale } from "../../../apiUtils/commonUtils";
import { getFromCookieMetadata } from "../../../apiUtils/cookieDataUtils";
import { getCookie } from "../../../apiUtils/cookieUtils";
import { localeConstants } from "../../../apiUtils/constants";

/**
 * @description This function is used to create wishlist
 *
 * @export
 * @param {string} channel
 * @return {*}  {(Promise<WishlistCreateResponse | null>)}
 */
export async function createWishlist(
  channel: string,
  lines: WishlistItemInput[],
): Promise<WishlistCreateResponse | null> {
  const saleorClient = new SaleorClient();
  const response: { data: CreateWishlistMutation } = await saleorClient.post(
    `${CreateWishlistDocument}`,
    {
      input: {
        channel,
        lines,
      },
      skipItems: true,
    },
    "no-store",
  );
  return response?.data?.wishlistCreateResponse || null;
}

/**
 * @description This function is used to get wishlist details
 *
 * @export
 * @param {string} wishlistId
 * @return {*}  {(Promise<WishlistResponse | null>)}
 */
export async function getWishlist(
  wishlistId: string,
  first: number,
  after?: string,
): Promise<WishlistResponse | null> {
  const saleorClient = new SaleorClient();

  if (!wishlistId) {
    return null;
  }
  const response: { data: WishlistDetailsQuery } = await saleorClient.post(
    `${WishlistDetailsDocument}`,
    {
      id: wishlistId,
      first,
      after,
      skipItems: false,
    },
    "no-store",
  );
  return response?.data?.wishlist || null;
}

export async function getWishlistedProducts(first: number): Promise<WishlistResponse | null> {
  const saleorClient = new SaleorClient();
  const locale = await getCookie(localeConstants.NEXT_LOCALE);
  const wishlistId: string = getFromCookieMetadata(getWishlistIdByLocale(locale) ?? "") ?? "";

  if (!wishlistId) {
    return null;
  }
  const response: { data: WishlistDetailsQuery } = await saleorClient.post(
    `${WishlistProductsDocument}`,
    {
      id: wishlistId,
      first,
    },
    "no-store",
  );
  return response?.data?.wishlist || null;
}

/**
 * @description This function is used to add item to wishlist.
 *
 * @export
 * @param {string} id - wishlist id
 * @param line: { variantId: string }
 * @return {*}  {(Promise<wishlistLinesAddResponse | null>)}
 */
export async function addToWishlist(
  id: string,
  variantId: string | string[],
  isDefaultVariant?: boolean,
  secondVarientId?: string,
): Promise<wishlistLinesAddResponse | null> {
  const saleorClient = new SaleorClient();
  const lines = [{ variantId, isDefaultVariant }];
  if (secondVarientId) {
    lines.push({ variantId: secondVarientId, isDefaultVariant });
  }

  const response: { data: AddToWishlistMutation } = await saleorClient.post(
    `${AddToWishlistDocument}`,
    {
      id,
      lines,
      skipItems: true,
    },
    "no-store",
  );
  revalidatePath("/favorites");
  revalidatePath("/my-account/favorites");
  return response?.data?.wishlistLinesAdd || null;
}

/**
 * @description This function is used to delete item to wishlist.
 *
 * @export
 * @param {string} id - wishlist id
 * @param line: { lineItemId }
 * @return {*}  {(Promise<wishlistItemDeleteResponse | null>)}
 */
export async function deleteWishlistLineItem(
  id: string,
  lineId: string,
): Promise<wishlistItemDeleteResponse | null> {
  const saleorClient = new SaleorClient();

  const response: { data: RemoveFromWishlistMutation } = await saleorClient.post(
    `${RemoveFromWishlistDocument}`,
    {
      id,
      linesIds: [lineId],
    },
    "no-store",
  );
  revalidatePath("/favorites");
  return response?.data?.wishlistItemDeleteResponse || null;
}

export async function notifyOutOfStockProduct(
  email: string,
  variantId: string,
  channel: string,
  productUrl: string,
  language: string,
): Promise<any | null> {
  const saleorClient = new SaleorClient();
  const response: { data: CustomerSubscriptionActivateMutation } = await saleorClient.post(
    `${CustomerSubscriptionActivateDocument}`,
    {
      email,
      channel,
      variantId,
      productUrl,
      language,
    },
    "no-store",
  );
  return response?.data?.customerSubscriptionActivate || null;
}
export async function mergeWishlist(id: string): Promise<any | null> {
  const saleorClient = new SaleorClient();
  const response: { data: MergeWishlistMutation } = await saleorClient.post(
    `${MergeWishlistDocument}`,
    {
      wishlistId: id,
    },
    "no-store",
  );
  return response?.data?.wishlistCustomerAttach || null;
}
